import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FieldError } from '@/components/ui/field';
import { FormFields } from '@/components/Inputs/FormFields';
import { makeFormFieldCreator } from '@/components/Inputs/FieldCreator';
import { useAuthState } from '@/utils/auth';
import { IdentityClaimKind, IncomingMerge } from '@/generated/codegen';
import { beginMergeSignInAsManaged } from '@/utils/identityClaimSession';
import { cognitoHasConfirmedEmail } from '@/utils/emailCode';
import { IDENTITY_CLAIM_ROUTE } from './identityClaimConstants';
import {
  GENERIC_ERROR_MESSAGE,
  refreshCurrentSession,
  signInForMerge,
} from './identityClaimCognito';

const SAME_USER_MESSAGE =
  'Ya estás con este usuario. Entrá con tu usuario de la compañía.';

type SignInValues = { username: string; password: string };
const fieldCreator = makeFormFieldCreator<SignInValues>();
const formFields = [
  fieldCreator({
    name: 'username',
    label: 'Usuario',
    inputProps: { autoComplete: 'username' },
  }),
  fieldCreator({
    name: 'password',
    label: 'Clave',
    inputProps: { type: 'password', autoComplete: 'current-password' },
  }),
];
const formSchema = Yup.object().shape({
  username: Yup.string().required('Campo obligatorio'),
  password: Yup.string().required('Campo obligatorio'),
});

export const IncomingMergeDialog: React.FC<{
  incoming: Pick<IncomingMerge, 'company_name' | 'managed_username'>;
}> = ({ incoming }) => {
  const [open, setOpen] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (
    values: SignInValues,
    { setSubmitting }: FormikHelpers<SignInValues>
  ) => {
    setError(null);
    try {
      await refreshCurrentSession();
      beginMergeSignInAsManaged();
    } catch {
      setError(GENERIC_ERROR_MESSAGE);
      setSubmitting(false);
      return;
    }
    const failure = await signInForMerge(
      values.username,
      values.password,
      SAME_USER_MESSAGE
    );
    if (!failure) return;
    setError(failure);
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {`Tenés un pedido para unir con tu usuario de ${incoming.company_name}`}
          </DialogTitle>
          <DialogDescription>
            Entrá con ese usuario para terminar: te queda uno solo, con tus
            partes, tus máquinas y tus roles.
          </DialogDescription>
        </DialogHeader>
        <Formik<SignInValues>
          initialValues={{ username: incoming.managed_username, password: '' }}
          validationSchema={formSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="flex flex-col gap-4">
              <FormFields fields={formFields} />
              {error && <FieldError>{error}</FieldError>}
              <Button type="submit" disabled={isSubmitting}>
                Entrar y unir
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
              >
                Ahora no
              </Button>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Picks up a claim left halfway: the code already confirmed in Cognito goes
 * to the password step, and a merge requested towards this user's email asks
 * to sign in as the managed user.
 */
export const IdentityClaimEntry: React.FC = () => {
  const { user } = useAuthState();
  const history = useHistory();
  const location = useLocation();
  const checked = React.useRef(false);

  const claim = user?.is_managed ? user.identity_claim : null;
  const resumeEmail =
    claim?.kind === IdentityClaimKind.FreeEmail ? claim.requested_email : null;
  const onClaimPage = location.pathname === IDENTITY_CLAIM_ROUTE;

  React.useEffect(() => {
    if (!resumeEmail || onClaimPage || checked.current) return;
    checked.current = true;
    cognitoHasConfirmedEmail(resumeEmail).then((confirmed) => {
      if (confirmed) history.push(IDENTITY_CLAIM_ROUTE);
    });
  }, [history, onClaimPage, resumeEmail]);

  return user?.incoming_merge ? (
    <IncomingMergeDialog incoming={user.incoming_merge} />
  ) : null;
};
